import { createSelector } from "@reduxjs/toolkit";
import { TaskRoute } from "tm-shared/types";

import { RootState } from "@/store";
import { selectParams, selectStringParam } from "@/store/selectors";

import { ProgressState, ProgressStateKeys } from "./progress.types";

const selectProgress = (s: RootState) => s.progress;
const selectCheckoutDetails = (s: RootState) => s.progress.checkoutDetails;
const selectTaskPointMap = (s: RootState) => s.work.taskPointMap;
const selectExistingTasks = (s: RootState) => s.work.tasks;

export const selectUncompletedTasks = createSelector(
    [selectProgress, selectTaskPointMap, selectExistingTasks],
    (progress, taskPointMap, existingTasks) => {
        const taskIds = Object.values(TaskRoute).flatMap((routeKey) =>
            Object.keys(progress[routeKey as keyof typeof progress] ?? {}),
        );

        return taskIds.flatMap((taskId) => {
            const pointId = taskPointMap[taskId];
            if (!pointId) return [];

            const task = existingTasks?.[pointId]?.[taskId];
            if (!task || task.completed) return [];

            return [task];
        });
    },
);

// Task Payload
interface SelectTaskPayloadParams {
    taskRoute: TaskRoute;
    taskId: string;
}

type TaskPayloadOf<R extends TaskRoute & ProgressStateKeys> =
    ProgressState[R] extends Record<string, infer V> ? V : never;

const selectTaskPayloadInternal = createSelector(
    [selectProgress, selectParams<SelectTaskPayloadParams>],
    (progress, { taskId, taskRoute }) =>
        progress?.[taskRoute as keyof typeof progress]?.[taskId],
);

export function selectTaskPayload<R extends TaskRoute & ProgressStateKeys>(
    state: RootState,
    params: { taskRoute: R; taskId: string },
): TaskPayloadOf<R> | undefined {
    return selectTaskPayloadInternal(state, params) as
        | TaskPayloadOf<R>
        | undefined;
}

export const selectCheckoutDetailsByOrderId = createSelector(
    [selectCheckoutDetails, selectStringParam],
    (checkoutDetails, orderId) => checkoutDetails?.[orderId],
);
